import { Box, Slider, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';

import TextField from 'components/text-field';

import Divider from 'components/divider';
import { FeedReserveMarks } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/constants/feed-reserve-marks';
import { useInputSettingsState } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/context/input-settings-context';
import {
  bufferKgValue,
  calculateMaxDays,
  calculateZIndexValues,
  increasedDays,
} from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/utils/feed-reserve-calculations';
import {
  BufferFill,
  BufferText,
  OutlinedBox,
  OutlineText,
} from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/utils/styled-components';
import LineDescription from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/input-settings/feed-reserve/line-description';

type SliderProps = {
  bufferValue: number;
  newValue: any;
};

export type CreateFeedReserveForm = {
  dailyConsumtionRate: number;
  maxSiloCapacity: number | undefined;
};

const EditFeedReserve: React.FC<SliderProps> = () => {
  const intl = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const PAGE_NAME_ID = 'orderFeed.ofdMySupplyPlan.inputSettings.feedReserveTab';
  const {
    getOfdSettingsQuery: { data: ofdSettings },
    stateHandlers: { setFeedReserveInput: setFeedReserve },
  } = useInputSettingsState();

  const maxSiloCapacity = ofdSettings?.siloCapacity ?? 0;
  const dailyConsumptionRate = ofdSettings?.currentConsumptionRate ?? 0;
  const [days, setDays] = useState<number>(ofdSettings?.consumptionBufferDays ?? 0);
  const [bufferValue, setBufferValue] = useState<number>((ofdSettings?.capacityBuffer ?? 0) * 100);

  const staticBufferKg = useMemo(() => bufferKgValue(maxSiloCapacity, bufferValue, true), [maxSiloCapacity, bufferValue]);
  const maxDays = useMemo(() => calculateMaxDays(maxSiloCapacity, dailyConsumptionRate), [maxSiloCapacity, dailyConsumptionRate]);
  const zIndexValues = useMemo(() => calculateZIndexValues(bufferValue, maxDays, days), [bufferValue, maxDays, days]);
  const daysInKg = useMemo(
    () => increasedDays(days, dailyConsumptionRate, bufferValue, maxSiloCapacity),
    [days, dailyConsumptionRate, bufferValue, maxSiloCapacity]
  );
  const daysHeight = days > maxDays ? 1 : Math.min(daysInKg / maxSiloCapacity, 1);
  const showConsumptionsKgDays = dailyConsumptionRate * days;
  const totalBuffer = Number(staticBufferKg) + Number(showConsumptionsKgDays);

  // #region HANDLERS
  const handleSliderChange = (event: any, newValue: any) => {
    setBufferValue(newValue);
    setFeedReserve({ capacityBuffer: newValue / 100, consumptionBufferDays: days });
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    if (Number(inputValue) > 20) return;

    setBufferValue(Number(inputValue));
    setFeedReserve({ capacityBuffer: Number(inputValue) / 100, consumptionBufferDays: days });
  };

  const handleDaysInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = Number(event.target.value);
    setDays(inputValue);

    setFeedReserve({ capacityBuffer: bufferValue / 100, consumptionBufferDays: inputValue });
  };

  useEffect(() => {
    setFeedReserve({ capacityBuffer: bufferValue / 100, consumptionBufferDays: days });
  }, []);

  // #endregion HANDLRES

  return (
    <form>
      <Box p={5} display={'flex'} flexDirection={'row'} flexWrap={isMobile ? 'wrap' : 'nowrap'}>
        <Box width={'100%'} display={'flex'} flexDirection={'column'} justifyContent={'center'}>
          <Box mb={8} p={4} borderRadius={2} sx={{ background: theme.palette.green.o25 }}>
            <Box display={'flex'} justifyContent={'space-between'}>
              <Box>
                <FormattedMessage id={`${PAGE_NAME_ID}.totalBufferTitel`} />
              </Box>

              <Box>
                <FormattedNumber
                  value={totalBuffer}
                  unit={'kilogram'}
                  style="unit"
                  minimumFractionDigits={0}
                  maximumFractionDigits={0}
                />
              </Box>
            </Box>
          </Box>
          <Divider dark />
          <TextField
            sx={{ mt: 8 }}
            type="number"
            fullWidth
            value={ofdSettings?.consumptionBufferDays}
            endAdornment="days of consumption"
            dark
            label={intl.formatMessage({ id: `${PAGE_NAME_ID}.adaptiveBufferTitel` })}
            onChange={handleDaysInputChange}
            error={daysInKg > maxSiloCapacity}
          />
          <Typography fontSize={14} sx={{ pt: 2 }}>
            <FormattedMessage
              id={`${PAGE_NAME_ID}.adaptiveBufferText`}
              defaultMessage={''}
              values={{
                days: days,
                increasedDays: (
                  <FormattedNumber
                    value={showConsumptionsKgDays}
                    unit={'kilogram'}
                    style="unit"
                    minimumFractionDigits={0}
                    maximumFractionDigits={0}
                  />
                ),
              }}
            />
          </Typography>
          <OutlinedBox>
            <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'} pb={4}>
              <Typography variant="h5" mr={2}>
                <FormattedMessage id={`${PAGE_NAME_ID}.staticBuffer`} />
              </Typography>
              <Box display={'flex'} alignItems={'center'} gap={2}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    padding: theme.spacing(3),
                    backgroundColor: theme.palette.lightBlue.o25,
                    borderRadius: theme.spacing(2),
                  }}>
                  <Typography variant="h5" mr={2} fontWeight={500}>
                    <FormattedNumber value={Number(Math.floor(staticBufferKg))} /> kg
                  </Typography>
                </Box>
                <TextField
                  sx={{
                    border: 'solid 1px black',
                    borderRadius: 2,
                    height: 'fit-content',
                    input: {
                      width: '50px !important',
                      padding: 2,
                    },
                  }}
                  dark
                  label=""
                  value={bufferValue}
                  onChange={handleInputChange}
                  endAdornment={'%'}
                />
              </Box>
            </Box>
            <Slider
              value={bufferValue > 20 ? 0 : bufferValue}
              getAriaValueText={(e) => `${e}%`}
              valueLabelFormat={(e) => `${e.toFixed(0)}%`}
              valueLabelDisplay="on"
              marks={FeedReserveMarks}
              onChange={handleSliderChange}
              aria-label="Always visible"
              min={0}
              max={20}
            />
          </OutlinedBox>
          <Typography fontSize={14} sx={{ pt: 2 }}>
            <FormattedMessage id={`${PAGE_NAME_ID}.additionalBufferText`} />
          </Typography>
        </Box>
        <Box m={theme.spacing(0, 2, 2, 10)} minWidth={230} display={'flex'} flexDirection={'column'} justifyContent={'flex-end'}>
          <Box border={'solid black 1px'} borderRadius={2} p={theme.spacing(0, 0, 6, 0)} sx={{ backgroundColor: '#012231' }}>
            <BufferText>
              <FormattedMessage id={`${PAGE_NAME_ID}.maxCapacity`} />
              <FormattedNumber value={Number(ofdSettings?.siloCapacity)} maximumFractionDigits={0} /> kg
            </BufferText>
            <Box
              height={'240px'}
              display={'flex'}
              flexDirection={'column-reverse'}
              position={'relative'}
              pt={2}
              sx={{ backgroundColor: theme.palette.common.white }}>
              <BufferFill
                sx={{
                  height: bufferValue / 100,
                  zIndex: zIndexValues.buffer,
                  background: '#40A180',
                  borderTop: 'solid black 1px',
                }}>
                <OutlineText
                  sx={{
                    right: 8,
                    top: -13,
                  }}>
                  <FormattedMessage id={`${PAGE_NAME_ID}.staticTitleBuffer`} />
                  &nbsp;
                  <FormattedNumber
                    value={bufferValue}
                    unit={'percent'}
                    style="unit"
                    minimumFractionDigits={0}
                    maximumFractionDigits={0}
                  />
                </OutlineText>
              </BufferFill>
              <BufferFill
                sx={{
                  height: daysHeight,
                  background: theme.palette.green.o50,
                  zIndex: zIndexValues.days,
                  borderTop: 'dashed black 1px',
                }}>
                <OutlineText
                  sx={{
                    left: 8,
                    top: daysHeight < 0.93 ? -13 : 11,
                    borderRadius: daysHeight < 0.93 ? theme.spacing(2, 2, 0, 0) : theme.spacing(0, 0, 2, 2),
                  }}>
                  <FormattedMessage id={`${PAGE_NAME_ID}.adaptiveBuffer`} /> {days} days
                </OutlineText>
              </BufferFill>
            </Box>
          </Box>
          <LineDescription />
        </Box>
      </Box>
    </form>
  );
};

export default EditFeedReserve;
